import { useEffect } from "react";

/**
 * Deters casual copying of images/video on public pages:
 * - blocks right-click / long-press save on media
 * - blocks dragging media out of the page
 * - blocks save/print/devtools keyboard shortcuts
 * - blanks media on print and while a screen-capture key is pressed
 *
 * Note: browsers cannot truly block OS-level screenshots or screen recording,
 * so this raises the effort required rather than making it impossible.
 */
export function useMediaProtection(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const root = document.documentElement;
    root.classList.add("media-protected");

    const isMedia = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el || !el.closest) return false;
      return Boolean(el.closest("img, video, picture, canvas, [data-protected-media]"));
    };

    const onContextMenu = (e: MouseEvent) => {
      if (isMedia(e.target)) e.preventDefault();
    };

    const onDragStart = (e: DragEvent) => {
      if (isMedia(e.target)) e.preventDefault();
    };

    const onCopy = (e: ClipboardEvent) => {
      if (isMedia(e.target)) e.preventDefault();
    };

    const shield = (ms = 1200) => {
      root.classList.add("media-hidden");
      window.setTimeout(() => root.classList.remove("media-hidden"), ms);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key?.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;

      // Screenshot keys — blank media and wipe the clipboard image where possible
      if (key === "printscreen" || (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(key))) {
        shield(1500);
        try {
          navigator.clipboard?.writeText("");
        } catch {
          /* clipboard access may be denied */
        }
        return;
      }

      // Save / print page
      if (mod && (key === "s" || key === "p")) {
        e.preventDefault();
        return;
      }

      // Devtools shortcuts
      if (key === "f12" || (mod && e.shiftKey && ["i", "j", "c"].includes(key))) {
        e.preventDefault();
      }
    };

    const onBeforePrint = () => root.classList.add("media-hidden");
    const onAfterPrint = () => root.classList.remove("media-hidden");

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("copy", onCopy);
    document.addEventListener("keyup", onKeyDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);

    return () => {
      root.classList.remove("media-protected", "media-hidden");
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("keyup", onKeyDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [enabled]);
}
